// @flow
import React from 'react'
import classNames from 'classnames'
import I18n from 'i18n-react'

// types
import type { TabType } from '../Types/TabType'

type TabsProps = {
  activeKey: string,
  children: HTMLElement,
  className?: string,
  onChangeTab: (activeKey: string) => void,
  tabs: Array<TabType>,
  translate?: boolean,
}

export default class Tabs extends React.Component<TabsProps> {
  scrollHorizontal = React.createRef()

  static defaultProps = {
    className: '',
    translate: true,
    disabled: false,
  }

  componentDidUpdate(prevProps: TabsProps) {
    if (JSON.stringify(prevProps.tabs) !== JSON.stringify(this.props.tabs)) {
      this.init(this.props)
    }
  }

  init = (props: TabsProps) => {
    if (props.tabs && props.tabs.length > 0) {
      this.handleOnChangeTab(props.tabs[0].key)
    }
  }

  scrollToTab = (index: number) => {
    if (this.scrollHorizontal.current) {
      const lis = document.getElementById('tabs').childNodes
      if (lis && lis[index]) {
        let left = lis[index].offsetLeft - lis[index].offsetWidth / 2
        this.scrollHorizontal.current.scrollTo({ top: 0, left })
      }
    }
  }

  handleOnChangeTab = (activeKey: string) => {
    if (activeKey !== this.props.activeKey) {
      this.props.onChangeTab(activeKey)
      const currentTabIndex = this.props.tabs.findIndex((tab) => tab.key === activeKey)
      this.scrollToTab(currentTabIndex)
    }
  }

  handleOnChangePrevTab = () => {
    const { tabs, activeKey, onChangeTab } = this.props
    const prevTabIndex = tabs.findIndex((tab) => tab.key === activeKey) - 1
    if (tabs[prevTabIndex]) {
      onChangeTab(tabs[prevTabIndex].key)
      location.hash = `#tab-${prevTabIndex}`
      this.scrollToTab(prevTabIndex)
    }
  }

  handleOnChangeNextTab = () => {
    const { tabs, activeKey, onChangeTab } = this.props
    const nextTabIndex = tabs.findIndex((tab) => tab.key === activeKey) + 1
    if (tabs[nextTabIndex]) {
      onChangeTab(tabs[nextTabIndex].key)
      location.hash = `#tab-${nextTabIndex}`
      this.scrollToTab(nextTabIndex)
    }
  }

  render() {
    return (
      <div className={this.props.className}>
        <div className={`nav-tabs-wrapper mt-5 ${this.props.disabled ? 'grayscale' : ''}`}>
          <a onClick={this.handleOnChangePrevTab}>
            <div className="nav-btn prev" />
          </a>
          <div className="mobile-overflow start" />
          {this.props.tabs.length > 1 && (
            <ul className="nav nav-tabs" id="tabs" ref={this.scrollHorizontal} role="tablist">
              {this.props.tabs.map((tab: TabType, index) => (
                <li className="nav-item" key={tab.key}>
                  {!this.props.disabled ? (
                    <a
                      aria-controls={`tab-${index}`}
                      aria-selected={this.props.activeKey === tab.key ? 'true' : 'false'}
                      className={classNames(['nav-link', { active: this.props.activeKey === tab.key }])}
                      data-toggle="tab"
                      href={`#tab-${index}`}
                      id={`tab-${index}-title`}
                      onClick={() => {
                        this.handleOnChangeTab(tab.key)
                      }}
                      role="tab"
                    >
                      {this.props.translate ? I18n.translate(tab.label) : tab.label}
                      {!!tab.subLabel && <span className="d-flex small">{tab.subLabel}</span>}
                    </a>
                  ) : (
                    <p className="nav-link disabled">
                      {this.props.translate ? I18n.translate(tab.label) : tab.label}
                      {!!tab.subLabel && <span className="d-flex small">{tab.subLabel}</span>}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="mobile-overflow end" />
          <a onClick={this.handleOnChangeNextTab}>
            <div className="nav-btn next" />
          </a>
        </div>
        <div className="tab-content" id="tabs-content">
          {React.Children.map(this.props.children, (child) => {
            const classes = [
              'tab-pane fade',
              { 'show active': !child.key || child.key.includes(this.props.activeKey) },
              child.props && child.props.className ? child.props.className : null,
            ]
            if (child.key && !child.key.includes(this.props.activeKey)) {
              return null
            }
            return React.cloneElement(child, {
              className: classNames(classes),
            })
          })}
        </div>
      </div>
    )
  }
}
